/**
 * OMON-195 Part A — pure password-change helpers (no Firebase/DOM imports), so
 * they're unit-testable without dragging in firebase.ts's build-time --define
 * constants. The DOM/Firebase wiring lives in account.ts.
 */

/** We ask for ≥8; Firebase itself only enforces ≥6. */
export const MIN_PASSWORD_LENGTH = 8;

export interface PasswordValidation {
  ok: boolean;
  error?: string;
}

/**
 * Pure client-side validation of the change-password form. Order matters:
 * the earliest failing rule wins, so the message is the most actionable.
 */
export function validateChangePassword(
  current: string,
  next: string,
  confirm: string,
): PasswordValidation {
  if (!current) return { ok: false, error: "Enter your current password." };
  if (!next) return { ok: false, error: "Enter a new password." };
  if (next.length < MIN_PASSWORD_LENGTH) {
    return { ok: false, error: `New password must be at least ${MIN_PASSWORD_LENGTH} characters.` };
  }
  if (next === current) {
    return { ok: false, error: "New password must differ from your current one." };
  }
  if (next !== confirm) {
    return { ok: false, error: "New password and confirmation don't match." };
  }
  return { ok: true };
}

/** Map a Firebase Auth error code to a user-facing message. */
export function mapAuthError(code: string): string {
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Current password is incorrect.";
    case "auth/weak-password":
      return "New password is too weak — choose a longer one.";
    case "auth/requires-recent-login":
      return "Your session is too old — sign out and back in, then try again.";
    case "auth/too-many-requests":
      return "Too many attempts — wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Network error — check your connection and try again.";
    default:
      return "Couldn't change password. Please try again.";
  }
}
